import { useEffect, useMemo, useState } from "react";
import {
  AddProductCard,
  CategorySideMenu,
  FloatingMenuButton,
} from "../components";
import { StoreCategoryModel, StoreProductModel } from "../models";
import { useLayout } from "../context";
import { useStore } from "../hooks";
import { Col, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Element, scroller, animateScroll as scroll } from "react-scroll";
import Skeleton from "react-loading-skeleton";
import { Layout } from "../layouts";
import { isEmpty, ucwords } from "../helpers";
import React from "react";
import { FaChevronRight } from "react-icons/fa";

const MenuPage = () => {
  const [query, setQuery] = useState<string>("");

  const { setIsSidebarCollapsed } = useLayout();
  const { storeCategories, storeProducts } = useStore();
  const { categoryId, productId } = useParams();

  const searchFilteredProducts = useMemo(
    () =>
      storeProducts.filter((item) =>
        item.product_name.toLowerCase().includes(query.toLowerCase()),
      ),
    [query, storeProducts],
  );

  const filteredCategories = useMemo(() => {
    if (!storeCategories?.length) return [];
    return storeCategories.filter((item) => {
      const hasProduct = searchFilteredProducts?.some(
        (product) => product.cid === item.store_category_id,
      );

      const isTopLevel = !query && item.parent_id == "0";

      return hasProduct || isTopLevel;
    });
  }, [query, searchFilteredProducts, storeCategories]);

  const defaultCategoryId = useMemo(() => {
    const firstParentCategory = filteredCategories.find(
      (category) => category.parent_id == "0",
    );

    if (!firstParentCategory) return "";

    const firstSubCategory = filteredCategories.find(
      (category) => category.parent_id == firstParentCategory.store_category_id,
    );

    return (
      firstSubCategory?.store_category_id ||
      firstParentCategory.store_category_id
    );
  }, [filteredCategories]);

  const selectedCategoryId = categoryId || defaultCategoryId;

  const handleCategoryScroll = (id: any) => {
    scroller.scrollTo(`category-${id}`, {
      duration: 1200,
      spy: true,
      smooth: true,
      offset: -30,
      containerId: "store-menu",
    });
  };

  const handleProductScroll = (id: any) => {
    scroller.scrollTo(`product-${id}`, {
      duration: 1200,
      spy: true,
      smooth: true,
      offset: -30,
      containerId: "store-menu",
    });
  };

  useEffect(() => {
    setIsSidebarCollapsed(true);
  }, [setIsSidebarCollapsed]);

  useEffect(() => {
    if (productId) {
      handleProductScroll(productId);
    } else if (categoryId) {
      handleCategoryScroll(categoryId);
    } else {
      scroll.scrollToTop();
    }
  }, [categoryId, productId]);

  useEffect(() => {
    const scrollContainer = document.getElementById("store-menu");

    const handleStickyHeader = () => {
      const sections = document.querySelectorAll(".food_item_listing_row");

      sections.forEach((section: any) => {
        const stickyHeader = section.querySelector(".food_item_parent_sticky");

        if (!stickyHeader) return;

        const rect = section.getBoundingClientRect();

        if (rect.top <= 80) {
          stickyHeader.classList.add("active_sticky_header");
        } else {
          stickyHeader.classList.remove("active_sticky_header");
        }
      });
    };
    scrollContainer?.addEventListener("scroll", handleStickyHeader);

    handleStickyHeader();

    return () => {
      scrollContainer?.removeEventListener("scroll", handleStickyHeader);
    };
  }, []);

  const renderStickyHeader = (category: StoreCategoryModel) => (
    <div className="food_item_parent_sticky">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        {/* LEFT */}
        <div>
          <div className="subcategory_breadcrumb">
            <span className="text-primary">
              {" "}
              Home <FaChevronRight size={12} />{" "}
            </span>
            <span> Menu </span>
            <FaChevronRight size={12} />
            <span>{ucwords(category.category_name)}</span>
          </div>
          <h3 className="food_item_subtitle mb-1">
            {ucwords(category.category_name)}
          </h3>
          {/* <p className="subcategory_desc mb-0">
            Freshly prepared dishes crafted with premium ingredients and bold
            flavors.
          </p> */}
        </div>

        {/* RIGHT */}
        <div className="subcategory_header_actions">
          <div className="subcategory_item_count">
            <span>🛍️</span>
            <span>
              {
                searchFilteredProducts.filter(
                  (p) => p.cid === category.store_category_id,
                ).length
              }{" "}
              Items
            </span>
          </div>

          {/* <button className="filters_btn">
            <Filter size={16} />
            Filters
          </button> */}
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      {/* <ScrollableTabs tabs={storeMenu?.category} /> */}
      <div className="menu_page_header menu-flow">
        <div className="menu_page_header d-flex justify-content-start align-items-start">
          <CategorySideMenu
            selectedCategory={selectedCategoryId}
            categories={filteredCategories}
            handleSearch={(e: any) => setQuery(e.target.value)}
            handleScroll={handleCategoryScroll}
          />

          <div className="col d-flex justify-content-start menu_content flex-column">
            <div
              id="store-menu"
              className="menu-scroll-area position-relative overflow-y-auto food_item_listing_main col-md-12 px-4 py-2 px-lg-5 py-lg-3"
            >
              {/* Empty search result */}
              {query && searchFilteredProducts.length === 0 && (
                <div className="text-center fs-5">
                  No product found matching <b>"{query}"</b>
                </div>
              )}

              {/* Categories & Products */}
              {filteredCategories
                .filter((category) => category.parent_id == "0") // Parent categories only
                .map((parentCategory) => {
                  const subCategories = filteredCategories.filter(
                    (category) =>
                      category.parent_id == parentCategory.store_category_id,
                  );

                  // Function to render products for a given category
                  const renderProducts = (category: StoreCategoryModel) => (
                    <Row>
                      {searchFilteredProducts
                        ?.filter(
                          (product: StoreProductModel) =>
                            product.cid === category.store_category_id,
                        )
                        .map((product: StoreProductModel) => (
                          <Col
                            sm={12}
                            md={6}
                            key={product.id}
                            className="d-flex flex-column"
                          >
                            <Element
                              name={`product-${product.id}`}
                              className="d-flex flex-column flex-grow-1"
                            >
                              <AddProductCard
                                product={product}
                                category={category}
                              />
                              <div className="outline-sm"></div>
                            </Element>
                          </Col>
                        ))}
                    </Row>
                  );

                  // Subcategories exist → render them
                  if (subCategories.length > 0) {
                    return (
                      <React.Fragment key={parentCategory.store_category_id}>
                        {/* Parent category heading */}
                        <Element
                          name={`category-${parentCategory.store_category_id}`}
                          key={`parent-${parentCategory.store_category_id}`}
                        >
                          <div className="food_item_listing_row mb-0">
                            <h3>{ucwords(parentCategory.category_name)}</h3>
                            {/* Sticky Header */}
                            {renderStickyHeader(parentCategory)}
                          </div>
                        </Element>

                        {/* Subcategories */}
                        {subCategories.map((subCategory) => (
                          <Element
                            name={`category-${subCategory.store_category_id}`}
                            key={`sub-${subCategory.store_category_id}`}
                          >
                            <div className="food_item_listing_row">
                              <h3 className="food_item_subtitle">
                                {ucwords(subCategory.category_name)}
                              </h3>
                              {renderProducts(subCategory)}
                            </div>
                          </Element>
                        ))}
                      </React.Fragment>
                    );
                  }

                  // No subcategories → render parent
                  return (
                    <Element
                      name={`category-${parentCategory.store_category_id}`}
                      key={parentCategory.store_category_id}
                    >
                      <div className="food_item_listing_row">
                        <h3>{ucwords(parentCategory.category_name)}</h3>
                        {/* Sticky Header */}
                        {renderStickyHeader(parentCategory)}
                        {renderProducts(parentCategory)}
                      </div>
                    </Element>
                  );
                }) || (
                <Row>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
                    <Col sm={12} md={6} key={item}>
                      <Skeleton height={100} className="mb-3" />
                    </Col>
                  ))}
                </Row>
              )}

              {!isEmpty(filteredCategories) && (
                <FloatingMenuButton
                  categories={filteredCategories}
                  onSelect={handleCategoryScroll}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MenuPage;
